import React from "react";
import ButtonIcon from "../../../../components/buttons/ButtonIcon";
import CustomTooltip from "../../../../components/CustomTooltip";
import {
    IconExpandPanel,
    IconResizePanel,
    SettingsIcon,
    TrashOutlineIcon
} from "../../../../components/buttons/IconButtons";
import NotInformation from "../../../../components/NotInformation";
import { getPanelEmptyText } from "../Functionality/EventMonitoringCommon";
import PanelApp from "../Functionality/PanelApp";
import { PANEL_VIEW } from "../Model/Model";
import PanelEmptyState from "./PanelEmptyState";
import PanelLoading from "./PanelLoading";
import EventFeedRow from "./EventFeedRow";
import GroupedCard from "./GroupedCard";
import HighlightCard from "./HighlightCard";

/**
 * @Author      Karen Yunuen Pozos Arias
 * @JobTitle    Full Stack
 * @Email       kpozos@ibl.mx
 *
 * Panel of the canvas. Its body follows the view of its source: event feed, grouped overview,
 * highlight cards, or the empty state while it is unconfigured. The corner handle resizes it.
 */
const Panel = ({ panel, columns, rows, feed, now, t, onLoadMore, onLoadAreaEvents, onConfigure, onExpand, onDelete, onResize, onResizeEnd,
                   canCreate = false, canUpdate = false, canDelete = false }) => {
    const {
        panelRef,
        panelClassName,
        handleClassName,
        handleIconClassName,
        configured,
        view,
        header,
        panelEvents,
        bodyRef,
        showEmpty,
        loadingEvents,
        loadMoreIfNeeded,
        groups,
        handleResizeStart,
        setIsHandleHover
    } = PanelApp({ panel, columns, rows, feed, t, onLoadMore, onResize, onResizeEnd });

    const HeaderIcon = header.Icon;

    return (
        <div ref={panelRef} className={panelClassName}>
            <div className='event-monitoring-panel-header border-bottom-shades-200'>
                <div className='d-flex align-items-center gap-2 minw-0'>
                    <div className={`d-flex flex-shrink-0 align-items-center justify-content-center w-24px h-24px border-radius-4px ${header.pictogram.BACKGROUND_CLASS}`}>
                        <HeaderIcon className={`btn-icon-svg-16px ${header.pictogram.ICON_CLASS}`} />
                    </div>
                    <CustomTooltip
                        text={header.title}
                        classes='fs-14px ibl-font-w-600 font-color-general-950 text-ellipsis-1-row'
                        showIfTruncated
                        placement='top'
                    />
                </div>
                <div className='event-monitoring-panel-actions d-flex align-items-center gap-12px'>
                    <ButtonIcon
                        id={`btn_p_expand_${panel.id}`}
                        title='expand'
                        Icon={IconExpandPanel}
                        className='btn-outlined-shades-600'
                        iconProps='btn-icon-svg-16px'
                        disabled={!configured}
                        onClick={() => onExpand(panel)}
                    />
                    {canUpdate &&
                        <ButtonIcon
                            id={`btn_p_configure_${panel.id}`}
                            title='configure_panel'
                            Icon={SettingsIcon}
                            className='btn-outlined-shades-600'
                            iconProps='btn-icon-svg-16px'
                            disabled={!configured}
                            onClick={() => onConfigure(panel)}
                        />
                    }
                    {canDelete &&
                        <ButtonIcon
                            id={`btn_p_delete_${panel.id}`}
                            title='delete'
                            Icon={TrashOutlineIcon}
                            className='btn-outlined-error-600'
                            iconProps='btn-icon-svg-16px'
                            onClick={() => onDelete(panel)}
                        />
                    }
                </div>
            </div>

            {view === PANEL_VIEW.UNCONFIGURED &&
                <PanelEmptyState
                    legend='you_have_not_configured_this_panel_yet'
                    // Configuring a panel for the first time creates its data source //
                    action={canCreate ? 'configure_panel' : null}
                    onAction={() => onConfigure(panel)}
                />
            }

            {view !== PANEL_VIEW.UNCONFIGURED &&
                <div
                    ref={bodyRef}
                    onScroll={loadMoreIfNeeded}
                    className={`event-monitoring-panel-body custom-scrollbar ${showEmpty ? 'd-flex flex-column pb-3' : ''} ${!showEmpty && view === PANEL_VIEW.FEED ? 'event-monitoring-panel-body-feed' : ''}`}>
                    {showEmpty &&
                        <NotInformation
                            text={getPanelEmptyText(panel, t)}
                            card={true}
                            className='event-monitoring-panel-not-information'
                        />
                    }

                    {view === PANEL_VIEW.FEED && panelEvents.map((event) => (
                        <EventFeedRow key={event.id} event={event} now={now} />
                    ))}

                    {view === PANEL_VIEW.GROUPED && groups.length > 0 &&
                        <div className='d-flex flex-column gap-3'>
                            {groups.map((group) => (
                                <GroupedCard
                                    key={group.id}
                                    group={group}
                                    now={now}
                                    onLoadMore={() => onLoadAreaEvents(panel, group.id)}
                                />
                            ))}
                        </div>
                    }

                    {view === PANEL_VIEW.HIGHLIGHT &&
                        <div className='d-flex flex-column gap-3'>
                            {panelEvents.map((event) => (
                                <HighlightCard key={event.id} event={event} now={now} />
                            ))}
                        </div>
                    }

                    {loadingEvents && <PanelLoading />}
                </div>
            }

            {canUpdate &&
                <div
                    id={`div_p_resize_${panel.id}`}
                    className={handleClassName}
                    onPointerDown={handleResizeStart}
                    onMouseEnter={() => setIsHandleHover(true)}
                    onMouseLeave={() => setIsHandleHover(false)}
                >
                    <IconResizePanel className={handleIconClassName} />
                </div>
            }
        </div>
    );
};

export default Panel;
